// TherapistBooking.tsx
import React, { useEffect, useMemo, useState } from 'react'
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  Button,
  Autocomplete,
  Snackbar,
  Alert,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  Stack,
  Avatar,
  Divider,
} from '@mui/material'
import {
  WarningAmber as WarningIcon,
  Email as EmailIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material'
import { FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi'
import api from '@/services/api'
import { Package } from '@/data/mockData'
import DocuSignSigner from './DocSign'
import { useNavigate } from 'react-router-dom'
import { relationshipOptions } from '@/constants/familyConstants'
import { loadStripe } from '@stripe/stripe-js'
import {
  CardElement,
  useStripe,
  useElements,
  Elements,
} from '@stripe/react-stripe-js'
import { membershipApi } from '@/services/api'
import { useDebounce } from '@/hooks/useDebounce'
import Chip from '@mui/material/Chip'

// -------------------- Types --------------------
type PaymentType = 'insurance' | 'cash' | 'copay'

interface SlotItem {
  _id?: string
  startTime: string
  endTime: string
}

interface TherapistData {
  image: any
  name: string
  experience: React.ReactNode
  rate: React.ReactNode
  id?: string
  firstName?: string
  lastName?: string
  credentials?: string
  experienceYears?: number | string
  specialties?: string[]
  insurances?: string[]
  priceLabel?: string
  avatarUrl?: string | null
}

interface FamilyMember {
  _id: string
  patientId: string
  dependentPatientId: string
  firstName: string
  lastName: string
  relationship: string
  dateOfBirth: string
  email: string
  phone: string
  isDeleted: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
  __v: number
}

interface PatientProfile {
  _id: string
  firstName: string
  lastName: string
  email: string
  phone: string
  gender: string
  dob: string
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  country: string
  postalCode: string
}

interface Insurance {
  _id: string
  patientId: string
  insuranceName: string
  insuranceType: string
  payerId: string
  policyNumber: string
  groupNumber: string
  policyHolder: string
  copay: number
  expiryDate: string
  isDeleted: boolean
  insuranceFrontUrl: string
  status: string
}

interface Payer {
  _id: string
  payer_name: string
  payer_state: string
  payer_type: string
  workers_comp: string
  ub_claims: string
  eligibility: string
  auto: string
  attachment: string
}

interface PaymentMethod {
  _id: string
  patientId: string
  cardBrand: string
  last4: string
  expMonth: string
  expYear: string
  cardOwner: string
  isDefault: boolean
  isDeleted: boolean
  createdAt: string
  updatedAt: string
  __v: number
}

interface Props {
  therapistId?: string
  therapistData?: TherapistData
  selectedPackage?: Package | null
  defaultDate?: Date | null
  defaultSessionsCount?: number
  onClose?: () => void
  onComplete?: (payload: any) => void
  bidPrice?: string
  contractAmount?: string
  sessionLength?: string
}

// -------------------- Utilities --------------------
const pad = (n: number) => String(n).padStart(2, '0')
const formatDateKey = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const formatDateDisplayFull = (d: Date) =>
  d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
const formatDateDisplayLong = (d: Date) =>
  d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })

// Format date for display (MM/DD/YYYY)
const formatDateForDisplay = (dateString: string) => {
  const [year, month, day] = dateString.split("-")
  return `${month}/${day}/${year}`
}

// High z-index for dropdowns - using MUI's zIndex system
const dropdownZIndex = 9999

// Stripe configuration
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)

// Dropdown menu props with proper z-index
const getDropdownMenuProps = (zIndex: number = dropdownZIndex) => ({
  PaperProps: {
    sx: {
      zIndex: `${zIndex} !important`,
      position: 'relative',
    },
  },
  MenuListProps: {
    sx: {
      zIndex: zIndex,
    },
  },
  sx: {
    zIndex: zIndex,
  },
})

// Autocomplete popper props
const getAutocompletePopperProps = (zIndex: number = dropdownZIndex) => ({
  popper: {
    sx: {
      zIndex: `${zIndex} !important`,
    },
  },
  paper: {
    sx: {
      zIndex: `${zIndex} !important`,
    },
  },
})

// Stripe Card Input Component (inner component that uses Stripe hooks)
interface StripeCardInputInnerProps {
  cardholderName: string
  onCardholderNameChange: (name: string) => void
  cardComplete: boolean
  onCardCompleteChange: (complete: boolean) => void
  selectedPaymentMethod: PaymentMethod | null
  onStripeReady: (stripe: any, elements: any) => void
  isSaving: boolean
}

const StripeCardInputInner: React.FC<StripeCardInputInnerProps> = ({
  cardholderName,
  onCardholderNameChange,
  cardComplete,
  onCardCompleteChange,
  selectedPaymentMethod,
  onStripeReady,
  isSaving,
}) => {
  const stripe = useStripe()
  const elements = useElements()

  // Expose stripe and elements instances to parent
  React.useEffect(() => {
    if (stripe && elements) {
      onStripeReady(stripe, elements)
    }
  }, [stripe, elements, onStripeReady])

  const handleCardElementChange = (event: any) => {
    onCardCompleteChange(event.complete)
  }

  const cardElementOptions = {
    disableLink: true,
    style: {
      base: {
        fontSize: '14px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  }

  // If a saved payment method is selected, show its details instead of card input
  if (selectedPaymentMethod) {
    return (
      <div className="p-3 border border-gray-200 rounded-lg bg-gray-50">
        <div className="text-sm font-medium text-gray-700">
          Selected Payment Method
        </div>
        <div className="mt-1 text-xs text-gray-600">
          {selectedPaymentMethod.cardBrand.toUpperCase()} ****{' '}
          {selectedPaymentMethod.last4} ({selectedPaymentMethod.cardOwner})
        </div>
      </div>
    )
  }

  return (
    <div className="gap-4 space-y-4">
      <TextField
        size="small"
        label="Cardholder Name*"
        fullWidth
        value={cardholderName}
        onChange={e => onCardholderNameChange(e.target.value)}
        disabled={isSaving || !stripe}
        style={{
          marginBottom: '1rem',
        }}
      />
      <div
        className="p-3 border border-gray-300 rounded-lg"
        style={{
          borderColor: cardComplete ? '#3BAE78' : undefined,
        }}
      >
        {stripe && elements && (
          <CardElement
            options={cardElementOptions}
            onChange={handleCardElementChange}
          />
        )}
      </div>
      <div className="text-[11px] text-gray-500">
        Your card details are securely processed by Stripe
      </div>
    </div>
  )
}

// Wrapper component with Elements provider
interface StripeCardInputProps {
  cardholderName: string
  onCardholderNameChange: (name: string) => void
  cardComplete: boolean
  onCardCompleteChange: (complete: boolean) => void
  selectedPaymentMethod: PaymentMethod | null
  onStripeReady: (stripe: any, elements: any) => void
  isSaving: boolean
}

const StripeCardInput: React.FC<StripeCardInputProps> = props => {
  return (
    <Elements stripe={stripePromise}>
      <StripeCardInputInner {...props} />
    </Elements>
  )
}

const TherapistBooking: React.FC<Props> = ({
  therapistId,
  therapistData,
  selectedPackage,
  defaultDate = null,
  defaultSessionsCount,
  sessionLength,
  bidPrice,
  onClose,
  onComplete,
  contractAmount,
}) => {

  const ratePrice = parseInt(selectedPackage.rate.replace('$', ''), 10);
  const sessionLengthRemoveMin = parseInt(selectedPackage?.duration.replace(' min', ''), 10);



  const therapist = therapistData ?? null
  const therapistRate = Number(String(therapist.rate).replace('$', ''));
  const navigate = useNavigate()
  // Calendar / month
  const today = useMemo(() => new Date(), [])
  const [monthOffset, setMonthOffset] = useState(0)
  const monthStart = useMemo(
    () => new Date(today.getFullYear(), today.getMonth() + monthOffset, 1),
    [today, monthOffset]
  )
  const daysInMonth = useMemo(
    () =>
      new Date(
        monthStart.getFullYear(),
        monthStart.getMonth() + 1,
        0
      ).getDate(),
    [monthStart]
  )
  const monthDates = useMemo(() => {
    const arr: (Date | null)[] = []
    // Get the day of the week for the first day of the month (0 = Sunday, 1 = Monday, etc.)
    const firstDayOfWeek = monthStart.getDay()

    // Add null entries for days before the first day of the month
    for (let i = 0; i < firstDayOfWeek; i++) {
      arr.push(null)
    }

    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      arr.push(new Date(monthStart.getFullYear(), monthStart.getMonth(), i))
    }

    return arr
  }, [monthStart, daysInMonth])

  // Sessions & selection
  // Use defaultSessionsCount if it's a positive number, otherwise default to 5
  const [sessionsCount, setSessionsCount] = useState<number>(5)
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    defaultDate ?? today
  )
  const [slotsForDate, setSlotsForDate] = useState<SlotItem[]>([])
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [slotFetchError, setSlotFetchError] = useState<string | null>(null)

  // Dynamic data states
  const [patientProfile, setPatientProfile] = useState<PatientProfile | null>(
    null
  )
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([])
  const [insurances, setInsurances] = useState<Insurance[]>([])
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [loadingData, setLoadingData] = useState(true)

  // Selected values
  const [selectedSlots, setSelectedSlots] = useState<
    Record<string, { slotId?: string; startTime: string; endTime: string }>
  >({})
  const [bookingFor, setBookingFor] = useState<'self' | string>('self')
  const [selectedInsurance, setSelectedInsurance] = useState<Insurance | null>(
    null
  )
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethod | null>(null)

  // Form fields
  const [patientFirstName, setPatientFirstName] = useState('')
  const [patientLastName, setPatientLastName] = useState('')
  const [patientEmail, setPatientEmail] = useState('')
  const [patientPhone, setPatientPhone] = useState('')
  const [patientDob, setPatientDob] = useState('')
  const [patientGender, setPatientGender] = useState('')
  const [relationship, setRelationship] = useState('')
  const [patientId, setPatientId] = useState('')
  const [familyMemberId, setFamilyMemberId] = useState('')
  const [contractId, setContractId] = useState('')

  // Address
  const [residentialAddress, setResidentialAddress] = useState('')
  const [city, setCity] = useState('')
  const [stateValue, setStateValue] = useState('')
  const [zip, setZip] = useState('')

  // Insurance
  const [insuranceMemberId, setInsuranceMemberId] = useState('')
  // New insurance search (payer search)
  const [payerSearchInput, setPayerSearchInput] = useState('')
  const [payerSearchOptions, setPayerSearchOptions] = useState<Payer[]>([])
  const [isSearchingPayers, setIsSearchingPayers] = useState(false)
  const [selectedNewPayer, setSelectedNewPayer] = useState<Payer | null>(null)
  const [policyId, setPolicyId] = useState('')

  // Card (using Stripe Elements)
  const [cardholderName, setCardholderName] = useState('')
  const [cardComplete, setCardComplete] = useState(false)
  const [isSavingNewCard, setIsSavingNewCard] = useState(false)
  const [stripeInstance, setStripeInstance] = useState<any>(null)
  const [elementsInstance, setElementsInstance] = useState<any>(null)

  // Payment
  const [paymentType, setPaymentType] = useState<PaymentType>('insurance')

  // Agreements
  const [agreed, setAgreed] = useState(true)
  const [openDocuSign, setOpenDocuSign] = useState(false)

  // Snackbar states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  })

  // Loading state for booking
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Consent error state
  const [consentError, setConsentError] = useState<{
    open: boolean
    message: string
    familyMemberId: string | null
  }>({
    open: false,
    message: '',
    familyMemberId: null,
  })
  const [isResendingConsent, setIsResendingConsent] = useState(false)

  // -------------------- Snackbar Handler --------------------
  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'warning' | 'info' = 'success'
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    })
  }

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }))
  }

  // -------------------- Resend Consent Handler --------------------
  const handleResendConsent = async () => {
    if (!consentError.familyMemberId) {
      showSnackbar('Family member patient ID is missing', 'error')
      return
    }

    try {
      setIsResendingConsent(true)
      const response = await api.post('/contracts/resend-consent-patient', {
        patient_id: consentError.familyMemberId,
      })

      if (response.data) {
        showSnackbar(
          'Consent email has been resent successfully! Please ask the family member to check their email.',
          'success'
        )
        setConsentError({ open: false, message: '', familyMemberId: null })
      }
    } catch (error: any) {
      console.error('Error resending consent:', error)
      const errorMessage =
        error.response?.data?.message ||
        'Failed to resend consent email. Please try again.'
      showSnackbar(errorMessage, 'error')
    } finally {
      setIsResendingConsent(false)
    }
  }

  const handleCloseConsentError = () => {
    setConsentError({ open: false, message: '', familyMemberId: null })
  }

  // -------------------- Data Fetching --------------------
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingData(true)

        // Fetch patient profile
        const profileResponse = await api.get('/patient/profile')
        const profileData = profileResponse.data.data
        setPatientProfile(profileData)

        // Set patient information
        setPatientId(profileData._id)
        setPatientFirstName(profileData.firstName)
        setPatientLastName(profileData.lastName)
        setPatientEmail(profileData.email)
        setPatientPhone(profileData.phone)
        setPatientDob(formatDateForDisplay(profileData.dob))
        setPatientGender(profileData.gender)

        // Set address (but allow manual editing)
        setResidentialAddress(profileData.addressLine1 || '')
        setCity(profileData.city || '')
        setStateValue(profileData.state || '')
        setZip(profileData.postalCode || '')

        // Fetch family members
        const familyResponse = await api.get(
          `/patient/family/get-members-by-patient/${profileData._id}`
        )
        setFamilyMembers(familyResponse.data.data || [])

        // Fetch insurances
        const insuranceResponse = await api.get(
          '/patient/insurance/list-active?page=1&limit=100&search='
        )
        setInsurances(insuranceResponse.data.data || [])

        // Fetch payment methods
        const paymentResponse = await api.get(
          '/patient/payment-methods?page=1&limit=10'
        )
        setPaymentMethods(paymentResponse.data.data || [])
      } catch (error) {
        console.error('Error fetching data:', error)
        showSnackbar('Failed to load patient data', 'error')
      } finally {
        setLoadingData(false)
      }
    }

    fetchData()
  }, [])

  // Debounce search input for payer search
  const debouncedPayerSearch = useDebounce(payerSearchInput, 300)

  // Search payers when debounced input changes
  useEffect(() => {
    const searchPayers = async () => {
      if (debouncedPayerSearch.trim().length === 0) {
        setPayerSearchOptions([])
        setIsSearchingPayers(false)
        return
      }

      setIsSearchingPayers(true)

      try {
        const response = await membershipApi.searchPayers(debouncedPayerSearch)
        const payersData = response.data || response
        if (Array.isArray(payersData)) {
          setPayerSearchOptions(payersData)
        }
      } catch (error) {
        console.error('Error searching payers:', error)
      } finally {
        setIsSearchingPayers(false)
      }
    }

    searchPayers()
  }, [debouncedPayerSearch])

  // -------------------- Handlers for Dynamic Data --------------------
  const handleBookingForChange = (value: 'self' | string) => {
    setBookingFor(value)

    if (value === 'self' && patientProfile) {
      // Reset to self data
      setPatientFirstName(patientProfile.firstName)
      setPatientLastName(patientProfile.lastName)
      setPatientEmail(patientProfile.email)
      setPatientPhone(patientProfile.phone)
      setPatientDob(formatDateForDisplay(patientProfile.dob))
      setPatientGender(patientProfile.gender)
      setRelationship('')
      setFamilyMemberId('')
    } else if (value === 'new_member') {
      // Clear all fields for new member
      setPatientFirstName('')
      setPatientLastName('')
      setPatientEmail('')
      setPatientPhone('')
      setPatientDob('')
      setPatientGender('')
      setRelationship('')
      setFamilyMemberId('')
    } else {
      // Find and set family member data
      const familyMember = familyMembers.find(
        member =>
          member._id === value ||
          `${member.firstName} ${member.lastName}` === value
      )

      if (familyMember) {
        setPatientFirstName(familyMember.firstName)
        setPatientLastName(familyMember.lastName)
        setPatientEmail(familyMember.email)
        setPatientPhone(familyMember.phone)
        setPatientDob(formatDateForDisplay(familyMember.dateOfBirth))
        setPatientGender('') // Gender might not be available in family member data
        setFamilyMemberId(familyMember._id)
      }
    }
  }

  const handleInsuranceChange = (insurance: Insurance | null) => {
    setSelectedInsurance(insurance)
    // Clear new payer selection when saved insurance is selected
    if (insurance) {
      if (insurance.status === 'Pending') {
        showSnackbar("You can proceed with booking while your insurance verification is still pending. Please note that your first evaluation session will be self-pay, and coverage for follow-up visits will depend on your payer’s approval.", "warning")
      }
      setInsuranceMemberId(insurance.payerId)
      setSelectedNewPayer(null)
      setPolicyId('')
      setPayerSearchInput('')
    } else {
      setInsuranceMemberId('')
    }
  }

  const handleNewPayerChange = (payer: Payer | null) => {

    showSnackbar("You can proceed with booking while your insurance verification is still pending. Please note that your first evaluation session will be self-pay, and coverage for follow-up visits will depend on your payer’s approval.", "warning")

    setSelectedNewPayer(payer)
    // Clear saved insurance when new payer is selected
    if (payer) {
      setSelectedInsurance(null)
      setInsuranceMemberId('')
    }
  }

  const handlePaymentMethodChange = (method: PaymentMethod | null) => {
    setSelectedPaymentMethod(method)
    if (method) {
      // Clear card fields when a saved payment method is selected
      // We'll use payment_method_id instead of card details
      setCardholderName('')
      setCardComplete(false)
    } else {
      // Clear card fields when no payment method is selected
      setCardholderName('')
      setCardComplete(false)
    }
  }

  // -------------------- Helpers --------------------
  const isPastDate = (d: Date) => {
    const todayOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    )
    const compare = new Date(d.getFullYear(), d.getMonth(), d.getDate())
    return compare < todayOnly
  }

  // Extract service ID from composite ID format (therapistId-serviceId)
  // Package IDs may be in format "therapistId-serviceId", but API needs just the serviceId
  const extractServiceId = (
    packageId: string | number | undefined | null
  ): string | undefined => {
    if (!packageId) return undefined

    // Convert to string if it's a number
    const idString = String(packageId)

    // If the ID contains a hyphen, extract the part after it (serviceId)
    if (idString.includes('-')) {
      const parts = idString.split('-')
      // Return the last part (serviceId) - handles cases where therapistId might also contain hyphens
      return parts[parts.length - 1]
    }

    // If no hyphen, assume it's already just the serviceId
    return idString
  }

  // Slot fetching disabled - user does not select slots, contract is created with empty bookings
  // useEffect(() => { ... }, [selectedDate, therapistId, selectedPackage])

  // -------------------- Slot selection logic --------------------
  const handleSelectSlot = (date: Date, slot: SlotItem) => {
    const dateKey = formatDateKey(date)
    const start = slot.startTime
    const end = slot.endTime

    if (
      selectedSlots[dateKey] &&
      selectedSlots[dateKey].startTime === start &&
      selectedSlots[dateKey].endTime === end
    ) {
      const copy = { ...selectedSlots }
      delete copy[dateKey]
      setSelectedSlots(copy)
      return
    }

    const selectedCount = Object.keys(selectedSlots).length
    if (!selectedSlots[dateKey] && selectedCount >= sessionsCount) {
      showSnackbar(
        `You can select up to ${sessionsCount} sessions. Remove one to add another.`,
        'warning'
      )
      return
    }

    setSelectedSlots(prev => ({
      ...prev,
      [dateKey]: { slotId: slot._id, startTime: start, endTime: end },
    }))
  }

  const removeSelectedSlot = (dateKey: string) => {
    const copy = { ...selectedSlots }
    delete copy[dateKey]
    setSelectedSlots(copy)
  }

  // -------------------- Save new card via Stripe --------------------
  const saveNewCardViaStripe = async (): Promise<string | null> => {
    if (!stripeInstance || !elementsInstance) {
      throw new Error('Stripe is not initialized')
    }

    // Get CardElement instance
    const cardElement = elementsInstance.getElement(CardElement)
    if (!cardElement) {
      throw new Error('Card element not found')
    }

    // Create payment method using Stripe
    const { error, paymentMethod } = await stripeInstance.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: {
        name: cardholderName,
      },
    })

    if (error) {
      throw new Error(error.message)
    }

    if (!paymentMethod) {
      throw new Error('Failed to create payment method')
    }

    // Prepare payload for your API
    const payload = {
      cardOwner: cardholderName,
      cardBrand: paymentMethod.card?.brand || 'unknown',
      last4: paymentMethod.card?.last4 || '',
      expMonth: paymentMethod.card?.exp_month || 0,
      expYear: paymentMethod.card?.exp_year || 0,
      isDefault: false,
      stripePaymentMethodId: paymentMethod.id,
    }

    // Call your API to save payment method
    const response = await api.post('patient/payment-methods', payload)

    if (!response.data || !response.data.card) {
      throw new Error('Failed to save payment method')
    }

    // Return the payment method ID from your API
    return response.data.card._id || null
  }

  // -------------------- Final submit --------------------
  const validateAndComplete = async () => {
    const errors: string[] = []

    // Validation
    if (!patientFirstName.trim()) errors.push('Patient first name is required.')
    if (!patientLastName.trim()) errors.push('Patient last name is required.')
    if (!patientEmail.trim()) errors.push('Patient email is required.')
    if (!patientPhone.trim()) errors.push('Patient phone is required.')
    if (!patientDob.trim()) errors.push('Patient date of birth is required.')
    if (!patientGender?.trim()) errors.push('Patient gender is required.')
    if (bookingFor === 'new_member' && !relationship.trim())
      errors.push('Relationship is required for new member.')
    if (!residentialAddress.trim())
      errors.push('Residential address is required.')
    if (!city.trim()) errors.push('City is required.')
    if (!stateValue.trim()) errors.push('State is required.')
    if (!zip.trim()) errors.push('Zip code is required.')
    if (!agreed) errors.push('You must accept the policies to proceed.')

    // Payment specific validations
    if (paymentType === 'insurance' || paymentType === 'copay') {
      // Either saved insurance OR new payer must be selected
      if (!selectedInsurance && !selectedNewPayer) {
        errors.push(
          'Please select an insurance provider or search for a new one.'
        )
      }
      // If saved insurance is selected, member ID is required
      if (selectedInsurance && !insuranceMemberId.trim()) {
        errors.push('Member ID is required for insurance.')
      }
      // If new payer is selected, policy ID is required
      if (selectedNewPayer && !policyId.trim()) {
        errors.push('Member ID is required.')
      }
    }

    if (paymentType === 'cash' || paymentType === 'copay') {
      // Only require card details if no saved payment method is selected
      if (!selectedPaymentMethod) {
        if (!cardholderName.trim()) errors.push('Cardholder name is required.')
        if (!cardComplete) errors.push('Please complete card details.')
      }
    }

    if (errors.length > 0) {
      showSnackbar(errors.join('\n'), 'error')
      return
    }

    // Save new card via Stripe if needed and get payment_method_id
    let finalPaymentMethodId: string | undefined = undefined

    // if (paymentType === 'cash' || paymentType === 'copay') {
    if (selectedPaymentMethod) {
      // Use saved payment method
      finalPaymentMethodId = selectedPaymentMethod._id
    } else {
      // Need to save new card first
      try {
        setIsSavingNewCard(true)
        const paymentMethodId = await saveNewCardViaStripe()
        if (!paymentMethodId) {
          showSnackbar('Failed to save card. Please try again.', 'error')
          setIsSavingNewCard(false)
          return
        }
        finalPaymentMethodId = paymentMethodId
      } catch (error: any) {
        console.error('Error saving card:', error)
        showSnackbar(
          error.message || 'Failed to save card. Please try again.',
          'error'
        )
        setIsSavingNewCard(false)
        return
      } finally {
        setIsSavingNewCard(false)
      }
    }

    // Ensure we have a payment_method_id for cash/copay payment types
    if (!finalPaymentMethodId) {
      showSnackbar('Payment method is required for this payment type.', 'error')
      return
    }
    // }

    // Ensure finalPaymentMethodId is set for cash/copay (should never happen due to validation above, but safety check)
    if (
      (paymentType === 'cash' || paymentType === 'copay') &&
      !finalPaymentMethodId
    ) {
      console.error('Payment method ID is missing for cash/copay payment type')
      showSnackbar(
        'Payment method is required. Please select a saved payment method or enter card details.',
        'error'
      )
      return
    }

    // Prepare the final payload (empty bookings - user does not select slots)
    const bidContract = bidPrice ? bidPrice : ratePrice || therapistRate
    const bidSessionLength = sessionLength ? sessionLength : sessionLengthRemoveMin
    const payload = {
      therapist_id: therapistId,
      patientFirstname: patientFirstName,
      patientLastname: patientLastName,
      patientEmail: patientEmail,
      patientPhone: patientPhone,
      patientDob: patientDob,
      patientGender: patientGender,
      patientAddress: residentialAddress,
      patientCity: city,
      patientZip: zip,
      patientState: stateValue,
      contract_amount: bidContract,
      session_length: bidSessionLength,
      proposed_session_length: bidSessionLength,
      // member_id: insuranceMemberId || undefined,
      member_id:
        bookingFor === 'self'
          ? patientId
          : bookingFor === 'new_member'
            ? undefined
            : familyMemberId,
      session_no: sessionsCount.toString(),
      booking_for:
        bookingFor === 'self'
          ? 'self'
          : bookingFor === 'new_member'
            ? 'new_member'
            : 'family',
      ...(bookingFor === 'new_member' && { relationship }),
      is_agree: agreed,
      payment_method_type: paymentType === 'cash' ? 'card' : paymentType,
      // Always send payment_method_id (never null) - either from saved method or newly saved card
      payment_method_id: finalPaymentMethodId,

      // Insurance details - either saved insurance or new payer
      ...(selectedInsurance
        ? {
          insurance_card_id: selectedInsurance._id,
          policy_id: selectedInsurance.policyNumber.trim(),
        }
        : selectedNewPayer
          ? {
            insurance_card_id: null,
            payer_id: selectedNewPayer._id,
            policy_id: policyId.trim(),
          }
          : {}),
      service_id: extractServiceId(selectedPackage?.id) || undefined,
      bookings: [],
    }

    // Debug: Log the payload to see payment_method_id
    // console.log('Booking payload:', JSON.stringify(payload, null, 2))
    // console.log('Payment type:', paymentType)
    // console.log('Final payment method ID:', finalPaymentMethodId)

    try {
      setIsSubmitting(true)

      // Call the create contract API
      const response = await api.post('/contracts/create-contract', payload)

      if (response.data && response.data.message) {
        showSnackbar('Booking completed successfully!', 'success')

        // Call the onComplete callback if provided
        // if (onComplete) {
        //   onComplete(response.data);
        // }

        setContractId(response?.data?.contract._id)
        setOpenDocuSign(true)

        // // Close the modal after successful booking
        // setTimeout(() => {
        //   onClose?.();
        // }, 3000);
      } else {
        throw new Error('Invalid response from server')
      }
    } catch (error: any) {
      console.error('Booking error:', error)

      // Check if it's a consent error
      const errorType = error.response?.data?.type
      const errorMessage =
        error.response?.data?.message ||
        'Failed to complete booking. Please try again.'

      if (errorType === 'CONSENT_NOT_SIGNED') {
        // Find the family member to get their dependentPatientId (actual patient ID)
        let patientIdForConsent: string | null = null
        if (bookingFor !== 'self' && bookingFor !== 'new_member') {
          const familyMember = familyMembers.find(
            member => member._id === bookingFor || member._id === familyMemberId
          )
          // Use dependentPatientId (actual patient ID) for the API
          patientIdForConsent = familyMember?._id || null
        }

        // Show consent error dialog instead of snackbar
        setConsentError({
          open: true,
          message: errorMessage,
          familyMemberId: patientIdForConsent,
        })
      } else {
        // Show regular error in snackbar
        showSnackbar(errorMessage, 'error')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const selectedSlotsCount = Object.keys(selectedSlots).length

  // -------------------- Render --------------------
  if (loadingData) {
    return (
      <div className="w-full max-w-[980px] mx-auto bg-[#fafafa] rounded-2xl shadow-lg overflow-y-hidden flex flex-col h-[92vh]">
        <div className="flex items-center justify-center flex-1 p-6 overflow-y-auto">
          <div className="text-gray-600">Loading patient data...</div>
        </div>
      </div>
    )
  }

  const handleSigned = () => {
    setOpenDocuSign(false) // close docusign modal
    onClose?.() // close parent modal (booking modal)
    navigate('/contracts?fromBooking=true&tab=pending')
  }

  return (
    <>
      <div
        className="w-full max-w-[980px] mx-auto bg-[#fafafa] rounded-2xl shadow-lg overflow-y-hidden flex flex-col h-[92vh]"
        style={{ zIndex: 1 }}
      >
        {/* Body */}
        <div className="flex-1 p-6 overflow-y-auto">
          {/* Header */}
          <div className="relative flex items-start pb-6 border-b border-gray-200">
            <button
              onClick={() => onClose?.()}
              className="cursor-pointer absolute right-0 top-[-4] w-8 h-8 rounded-full bg-[#E6F3EB] text-[#2E8A5A] flex items-center justify-center text-sm font-semibold"
              aria-label="Close"
            >
              ×
            </button>

            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-40 h-40 overflow-hidden bg-gray-100 rounded-2xl">
                {therapist?.image ? (
                  <img
                    src={therapist?.image}
                    alt="therapist"
                    className="object-cover w-full h-full"
                  />
                ) : (
                  <span className="text-2xl font-semibold text-gray-700">
                    {(therapist.name ?? '').charAt(0)}
                  </span>
                )}
              </div>

              <div>
                <div className="text-2xl font-semibold text-gray-800">
                  {therapist.name}
                </div>
                <div className="text-sm text-gray-500">
                  {therapist.credentials || 'Licensed Therapist'},{' '}
                  {therapist?.experience}
                </div>
                <div className="text-sm text-[#3BAE78] mt-1">
                  ✅ {therapist?.specialties?.join(', ') || ''}
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  {therapist?.insurances?.join(', ')}
                </div>
                {/* <button className="mt-3 px-4 py-2 bg-[#3BAE78] text-white rounded-lg text-sm">
                  View Profile
                </button> */}
              </div>
            </div>

            <div className="mt-12 ml-auto text-right">
              {bidPrice ? (
                <div className="text-xl font-bold text-gray-800">
                  ${bidPrice}/session
                </div>
              ) : (
                <div className="text-xl font-bold text-gray-800">
                  {selectedPackage?.rate || therapist?.rate}/session
                </div>
              )}
              {sessionLength ? (
                <div className="mt-1 text-xs text-gray-500">
                  {sessionLength} minute/session
                </div>
              ) : (
                <div className="mt-1 text-xs text-gray-500">
                  {selectedPackage?.duration} /session
                </div>
              )}


            </div>
          </div>

          {/* Sessions - User only specifies number of sessions (no slot selection) */}
          <div className="py-4 mb-6 border-b border-gray-200">
            <div className="grid items-center gap-4 md:grid-cols-2">
              <div>
                <div className="text-sm font-medium text-gray-700">
                  Number of Sessions
                </div>
                <div className="text-xs text-gray-400">
                  How many sessions would you like to book.
                </div>
                <div className="flex items-center gap-3 mt-2">
                  {/* <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                      value={sessionsCount}
                      onChange={e => setSessionsCount(Number(e.target.value))}
                      MenuProps={{
                        sx: { zIndex: 999999 },
                        PaperProps: {
                          sx: {
                            zIndex: 999999,
                            maxHeight: 300,
                            '& .MuiMenuItem-root': {
                              minHeight: 36,
                            },
                          },
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                      }}
                    >
                      {Array.from({ length: 30 }, (_, i) => i + 1).map(n => (
                        <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl> */}
                  <TextField
                    size="small"
                    type="number"
                    value={sessionsCount}
                    onFocus={e => e.target.select()}
                    onChange={e => {
                      const value = e.target.value

                      // Allow empty (so backspace works)
                      if (value === "") {
                        setSessionsCount("" as any)
                        return
                      }

                      let num = Number(value)

                      if (num > 100) num = 100
                      if (num < 1) num = 1

                      setSessionsCount(num)
                    }}
                    inputProps={{ min: 1, max: 100 }}
                    sx={{ minWidth: 120 }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Contract Summary */}
          <div className="p-4 mb-6 bg-white border border-gray-100 rounded-2xl">
            <div className="text-sm font-medium text-gray-700 mb-2">
              Contract Summary
            </div>
            <div className="text-xs text-gray-500">
              Provider: {therapist.name ?? ''}
            </div>
            <div className="text-xs text-gray-500">
              Sessions: {sessionsCount}
            </div>
          </div>

          {/* Patient information */}
          <div className="p-6 mt-8 bg-white border border-gray-200 rounded-2xl">
            <div className="text-[15px] font-semibold text-gray-800 mb-2">
              Patient's information
            </div>

            {/* <div className="text-[13px] text-gray-500 mb-3">
              You are booking appointment for*
            </div> */}

            {/* Booking For Pills - Dynamic with family members */}
            <div className="flex flex-wrap gap-3 mb-5">
              {/* Self option */}
              {/* <button
                onClick={() => handleBookingForChange('self')}
                className={`
                  cursor-pointer px-4 py-2 rounded-lg text-sm border transition
                  ${
                    bookingFor === 'self'
                      ? 'bg-[#3BAE78] text-white border-[#3BAE78]'
                      : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                  }
                `}
              >
                Self
              </button> */}

              {/* Family members */}
              {/* {familyMembers.map(member => (
                <button
                  key={member._id}
                  onClick={() => handleBookingForChange(member._id)}
                  className={`
                    px-4 py-2 rounded-lg text-sm border transition
                    ${
                      bookingFor === member._id
                        ? 'bg-[#3BAE78] text-white border-[#3BAE78]'
                        : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                    }
                  `}
                >
                  {member.firstName} {member.lastName}
                </button>
              ))} */}

              {/* New Member option */}
              {/* <button
                onClick={() => handleBookingForChange('new_member')}
                className={`
                  px-4 py-2 rounded-lg text-sm border transition
                  ${
                    bookingFor === 'new_member'
                      ? 'bg-[#3BAE78] text-white border-[#3BAE78]'
                      : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                  }
                `}
              >
                New Member
              </button> */}
            </div>

            {/* Form fields */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <TextField
                size="small"
                label="Patient's first name*"
                fullWidth
                value={patientFirstName}
                onChange={e => setPatientFirstName(e.target.value)}
              />

              <TextField
                size="small"
                label="Patient's last name*"
                fullWidth
                value={patientLastName}
                onChange={e => setPatientLastName(e.target.value)}
              />

              <TextField
                size="small"
                label="Email*"
                fullWidth
                value={patientEmail}
                onChange={e => setPatientEmail(e.target.value)}
              />

              <TextField
                size="small"
                label="Phone Number*"
                fullWidth
                value={patientPhone}
                onChange={e => setPatientPhone(e.target.value)}
              />

              <TextField
                size="small"
                label="Date of birth (mm/dd/yyyy)*"
                fullWidth
                value={patientDob}
                onChange={e => setPatientDob(e.target.value)}
              />

              <FormControl
                fullWidth
                size="small"
                sx={{ zIndex: dropdownZIndex }}
              >
                <InputLabel>Patient's gender listed on insurance*</InputLabel>
                <Select
                  value={patientGender}
                  label="Patient's gender listed on insurance*"
                  onChange={e => setPatientGender(e.target.value)}
                  // MenuProps={getDropdownMenuProps()}
                  MenuProps={{
                    sx: { zIndex: 999999 },
                    PaperProps: {
                      sx: {
                        zIndex: 999999,
                      },
                    },
                  }}
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                  <MenuItem value="Prefer not to say">
                    Prefer not to say
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Relationship field - only show for new member */}
              {bookingFor === 'new_member' && (
                <FormControl
                  fullWidth
                  size="small"
                  sx={{ zIndex: dropdownZIndex }}
                >
                  <InputLabel>Relationship*</InputLabel>
                  <Select
                    value={relationship}
                    label="Relationship*"
                    onChange={e => setRelationship(e.target.value)}
                    MenuProps={{
                      sx: { zIndex: 999999 },
                      PaperProps: {
                        sx: {
                          zIndex: 999999,
                        },
                      },
                    }}
                  >
                    {relationshipOptions.map(option => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </div>
          </div>

          {/* Address */}
          <div className="p-6 mt-8 bg-white border border-gray-200 rounded-2xl">
            <div className="text-[15px] font-semibold text-gray-800 mb-4">
              Patient's residential address
            </div>

            <TextField
              size="small"
              label="Residential address*"
              fullWidth
              className="mb-4"
              value={residentialAddress}
              onChange={e => setResidentialAddress(e.target.value)}
            />

            <div className="grid grid-cols-1 gap-4 mt-4 md:grid-cols-3">
              <FormControl
                fullWidth
                size="small"
                sx={{ zIndex: dropdownZIndex }}
              >
                <TextField
                  size="small"
                  label="State*"
                  value={stateValue}
                  onChange={e => setStateValue(e.target.value)}
                  fullWidth
                />
              </FormControl>
              <TextField
                size="small"
                label="City*"
                value={city}
                onChange={e => setCity(e.target.value)}
                fullWidth
              />
              <TextField
                size="small"
                label="Zip code*"
                value={zip}
                onChange={e => setZip(e.target.value)}
                fullWidth
              />
            </div>
          </div>

          {/* Payment */}
          <div className="p-6 mt-8 bg-white border border-gray-200 rounded-2xl">
            <div className="text-[15px] font-semibold text-gray-800 mb-4">
              Select Payment Type
            </div>

            {/* Payment type pills */}
            <div className="flex flex-col gap-3 mb-5">
              {[
                {
                  key: 'insurance' as PaymentType,
                  label: 'Insurance-Billed (no patient payment expected)',
                  note: 'Your insurance is billed; no copay/coinsurance expected.',
                },
                {
                  key: 'cash' as PaymentType,
                  label: 'Self-Pay (out of pocket)',
                  note: 'You pay the full amount directly.',
                },
                {
                  key: 'copay' as PaymentType,
                  label: 'Insurance + Self-Pay (copay/coinsurance/deductible)',
                  note: 'Insurance is billed; you pay any copay, coinsurance, or deductible.',
                },
              ].map(pay => (
                <label
                  key={pay.key}
                  className={`
                    flex items-start justify-between p-4 rounded-xl border cursor-pointer transition
                    ${paymentType === pay.key
                      ? 'border-[#3BAE78] bg-[#EAF8F1]'
                      : 'border-gray-200 hover:bg-gray-50'
                    }
                  `}
                >
                  <div className="flex-1 pr-3">
                    <span className="block mb-1 text-sm font-medium text-gray-800">
                      {pay.label}
                    </span>
                    <span className="text-xs text-gray-600">{pay.note}</span>
                  </div>
                  <input
                    type="radio"
                    name="payment"
                    checked={paymentType === pay.key}
                    onChange={() => setPaymentType(pay.key)}
                    className="w-4 h-4 mt-0.5 flex-shrink-0"
                  />
                </label>
              ))}
            </div>

            {/* Footnote */}
            <div className="p-3 mb-5 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-start gap-2">
                <svg
                  className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-xs leading-relaxed text-gray-600">
                  <span className="font-medium text-gray-700">Note:</span> Final
                  patient responsibility depends on your plan's benefits and
                  approvals.
                </p>
              </div>
            </div>

            {/* Insurance fields */}
            {(paymentType === 'insurance' || paymentType === 'copay') && (
              <div className="rounded-xl p-5 bg-[#EAF8F1] border border-[#CFEBDD] mb-5">
                <div className="space-y-4">
                  {/* Saved Insurance Dropdown and Member ID side by side */}
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Autocomplete
                      size="small"
                      options={insurances}
                      getOptionLabel={(option) => option.insuranceName}
                      value={selectedInsurance}
                      onChange={(_, newValue) => handleInsuranceChange(newValue)}

                      renderOption={(props, option) => (
                        <Box component="li" {...props} display="flex" justifyContent="space-between" width="100%">
                          <Typography>{option.insuranceName}</Typography>

                          <Chip
                            label={option.status}
                            size="small"
                            sx={{
                              ml: 2,
                              fontWeight: 500,
                              backgroundColor:
                                option.status === 'Verified'
                                  ? '#16a34a'
                                  : option.status === 'Rejected'
                                    ? '#dc2626'
                                    : '#f59e0b',
                              color: '#fff'
                            }}
                          />
                        </Box>
                      )}

                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select saved insurance"
                          placeholder="Choose from saved insurance cards"
                        />
                      )}

                      componentsProps={getAutocompletePopperProps()}
                      sx={{ zIndex: dropdownZIndex }}
                    />
                    {selectedInsurance && (
                      <TextField
                        size="small"
                        label="Member ID*"
                        fullWidth
                        value={insuranceMemberId}
                        onChange={e => setInsuranceMemberId(e.target.value)}
                      />
                    )}
                  </div>

                  {/* Divider or "OR" text */}
                  {insurances.length > 0 && (
                    <div className="flex items-center gap-2 my-2">
                      <div className="flex-1 border-t border-gray-300"></div>
                      <span className="text-sm text-gray-500">OR</span>
                      <div className="flex-1 border-t border-gray-300"></div>
                    </div>
                  )}

                  {/* New Insurance Search */}
                  <div>
                    {selectedNewPayer ? (
                      // Show selected payer with option to change
                      <div className="space-y-2">
                        <div className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-sm font-medium text-gray-700">
                                Selected Insurance Provider
                              </div>
                              <div className="mt-1 text-xs text-gray-600">
                                {selectedNewPayer.payer_name}
                              </div>
                            </div>
                            <Button
                              size="small"
                              onClick={() => {
                                setSelectedNewPayer(null)
                                setPayerSearchInput('')
                                setPolicyId('')
                              }}
                              sx={{ textTransform: 'none' }}
                            >
                              Change
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <Autocomplete
                        size="small"
                        options={payerSearchOptions}
                        value={selectedNewPayer}
                        inputValue={payerSearchInput}
                        onChange={(_, newValue) =>
                          handleNewPayerChange(newValue)
                        }
                        onInputChange={(event, newInputValue, reason) => {
                          // Only update input when user is typing, not when clearing/resetting
                          if (reason === 'input') {
                            setPayerSearchInput(newInputValue)
                          } else if (reason === 'clear') {
                            setPayerSearchInput('')
                          }
                        }}
                        getOptionLabel={option => option.payer_name}
                        isOptionEqualToValue={(option, value) =>
                          option._id === value._id
                        }
                        loading={isSearchingPayers}
                        filterOptions={x => x} // Disable client-side filtering since we're using server-side search
                        noOptionsText={
                          payerSearchInput.trim().length === 0
                            ? 'Start typing to search for insurance providers'
                            : 'No insurance providers found'
                        }
                        renderInput={params => (
                          <TextField
                            {...params}
                            label="New Insurance"
                            placeholder="Search a new insurance"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isSearchingPayers ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        componentsProps={getAutocompletePopperProps()}
                        sx={{ zIndex: dropdownZIndex }}
                      />
                    )}
                  </div>

                  {/* Policy ID field for new payer */}
                  {selectedNewPayer && (
                    <div>
                      <TextField
                        size="small"
                        label="Member ID*"
                        fullWidth
                        value={policyId}
                        onChange={e => setPolicyId(e.target.value)}
                        placeholder="Enter your member ID"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Card details box */}
            <div className="rounded-xl p-5 bg-[#EAF8F1] border border-[#CFEBDD]">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-5 h-5 rounded-full bg-[#3BAE78] text-white flex items-center justify-center text-xs">
                  ✓
                </div>
                <div className="text-sm font-medium text-gray-800">
                  Add Card Information
                </div>
              </div>

              {/* Saved Payment Methods Dropdown */}
              <div className="mb-4">
                <Autocomplete
                  size="small"
                  options={paymentMethods}
                  getOptionLabel={option =>
                    `${option.cardBrand.toUpperCase()} **** ${option.last4} (${option.cardOwner})`
                  }
                  value={selectedPaymentMethod}
                  onChange={(_, newValue) =>
                    handlePaymentMethodChange(newValue)
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Select saved payment method"
                    />
                  )}
                  componentsProps={getAutocompletePopperProps()}
                  sx={{ zIndex: dropdownZIndex }}
                />
              </div>

              {/* Stripe Card Input */}
              <StripeCardInput
                cardholderName={cardholderName}
                onCardholderNameChange={setCardholderName}
                cardComplete={cardComplete}
                onCardCompleteChange={setCardComplete}
                selectedPaymentMethod={selectedPaymentMethod}
                onStripeReady={(stripe, elements) => {
                  setStripeInstance(stripe)
                  setElementsInstance(elements)
                }}
                isSaving={isSavingNewCard}
              />
            </div>
          </div>

          {/* Agreements */}
          <div className="mt-8 mb-24">
            <label className="flex items-start gap-3 text-[14px] text-gray-700 leading-5">
              <Checkbox
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
              />
              <div>
                I acknowledge and accept PillarThera's policies and consent to
                receive communications about my account and services.
                <div className="mt-1 text-xs text-gray-500">
                  By continuing, I agree to the Privacy Policy and Terms of Use.
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 flex items-center justify-end gap-3 p-4 bg-white border-t border-gray-200">
          <Button
            variant="outlined"
            onClick={() => onClose?.()}
            className="px-5 py-2 rounded-2xl"
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={validateAndComplete}
            disabled={isSubmitting}
            sx={{
              backgroundColor: '#3BAE78',
              '&:hover': { backgroundColor: '#33a76a' },
            }}
            className="px-6 py-2 rounded-2xl"
            startIcon={
              isSubmitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {isSubmitting ? 'Processing...' : 'Complete Booking'}
          </Button>
        </div>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{ zIndex: dropdownZIndex + 1 }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%', whiteSpace: 'pre-line' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>

      {/* Consent Error Dialog */}
      <Dialog
        open={consentError.open}
        onClose={handleCloseConsentError}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: dropdownZIndex + 10 }}
        PaperProps={{
          sx: {
            borderRadius: '16px',
          },
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                bgcolor: 'warning.light',
                width: 48,
                height: 48,
              }}
            >
              <WarningIcon sx={{ fontSize: 28, color: 'warning.main' }} />
            </Avatar>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: 'text.primary' }}
            >
              Consent Required
            </Typography>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            <Alert severity="warning" sx={{ borderRadius: '8px' }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {consentError.message}
              </Typography>
            </Alert>

            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                bgcolor: '#E3F2FD',
                border: '1px solid',
                borderColor: '#2196F3',
                borderRadius: '12px',
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <InfoIcon sx={{ color: '#1976D2', mt: 0.5, fontSize: 20 }} />
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: '#1565C0', mb: 1 }}
                  >
                    What you need to do:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#424242', lineHeight: 1.6 }}
                  >
                    The family member needs to check their email and sign the{' '}
                    <Box
                      component="span"
                      sx={{ fontWeight: 600, color: '#1565C0' }}
                    >
                      Family Member / Caregiver Access & HIPAA Disclosure
                      Consent
                    </Box>{' '}
                    form before you can complete this booking.
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            {consentError.familyMemberId && (
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.300',
                  borderRadius: '12px',
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <EmailIcon
                    sx={{ color: 'primary.main', mt: 0.5, fontSize: 20 }}
                  />
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}
                    >
                      Need to resend the email?
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', lineHeight: 1.6 }}
                    >
                      Click the button below to send the consent form email
                      again to the family member.
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2.5, gap: 2 }}>
          <Button
            onClick={handleCloseConsentError}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              px: 3,
            }}
            disabled={isResendingConsent}
          >
            Close
          </Button>
          {consentError.familyMemberId && (
            <Button
              onClick={handleResendConsent}
              variant="contained"
              disabled={isResendingConsent}
              startIcon={
                isResendingConsent ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <EmailIcon />
                )
              }
              sx={{
                backgroundColor: '#3BAE78',
                '&:hover': { backgroundColor: '#33a76a' },
                borderRadius: '8px',
                textTransform: 'none',
                px: 3,
              }}
            >
              {isResendingConsent ? 'Sending...' : 'Resend Consent Email'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <DocuSignSigner
        open={openDocuSign}
        onClose={() => {
          setOpenDocuSign(false)
          onClose?.()
        }}
        onSigned={handleSigned}
        contractId={contractId}
        fromBooking={true}
      />
    </>
  )
}

export default TherapistBooking
